import { useCallback } from 'react';

import {
  getAddeparIntegrationLoginPath,
  getAddeparIntegrationLogoutPath,
} from '@/modules/assetProviderIntegrations/addepar/utils/addeparAuthUtils';
import { ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES } from '@/modules/assetProviderIntegrations/shared/constants';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { IntegrationProvider } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { IntegrationRow } from '../IntegrationRow';

interface AddeparCardProps {
  userAccountLinked: boolean;
  externalUserName?: string | null;
  integrationConfigured: boolean;
}

export function AddeparCard({
  userAccountLinked,
  externalUserName,
  integrationConfigured,
}: AddeparCardProps) {
  const canEditHouseholdData = useCanEditHouseholdData();
  const { assetIntegrationsEnabled } = useTenantDetailsContext();
  const currentUser = useCurrentUser();
  const isExpired =
    currentUser?.userExpiredIntegrationNames?.includes(
      ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.ADDEPAR
    ) ?? false;

  const getAddeparButtonProps = useCallback(() => {
    if (
      !canEditHouseholdData ||
      !assetIntegrationsEnabled ||
      !integrationConfigured
    ) {
      return { disabled: true, href: '#', children: 'Link account' };
    }

    const currentPageURL = getCompletePathFromRouteKey('USER_SETTINGS', {});
    if (userAccountLinked) {
      return {
        href: getAddeparIntegrationLogoutPath({ redirect: currentPageURL }),
        children: 'Unlink',
        method: 'post' as const,
      };
    } else {
      return {
        href: getAddeparIntegrationLoginPath({ redirect: currentPageURL }),
        children: 'Link account',
      };
    }
  }, [
    canEditHouseholdData,
    assetIntegrationsEnabled,
    integrationConfigured,
    userAccountLinked,
  ]);

  return (
    <IntegrationRow
      integrationEnabled={userAccountLinked}
      isExpired={isExpired}
      externalUserName={externalUserName}
      description={
        !integrationConfigured ? 'Contact your administrator' : undefined
      }
      logo={
        <img
          alt="Addepar"
          height={24}
          src={getPublicImageUrl('/addeparLogo.svg')}
        />
      }
      buttonProps={getAddeparButtonProps()}
      integrationProvider={IntegrationProvider.Addepar}
    />
  );
}
